import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiSave, FiSend, FiPlus, FiTrash2, 
  FiUser, FiPackage, FiPercent, FiFileText, FiX, 
  FiDownload, FiEye, FiAlertCircle,
  FiClock, FiCheckCircle
} from 'react-icons/fi';
import { format, addDays, differenceInDays } from 'date-fns';
import { toast } from 'react-toastify';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import { v4 as uuidv4 } from 'uuid';
import styles from './QuoteCreate.module.css';

interface QuoteForm {
  clientId: string;
  issueDate: Date;
  validUntil: Date;
  items: QuoteItemForm[];
  notes?: string;
  terms?: string;
  discountType: 'percentage' | 'fixed';
  discountValue: number;
  taxRate: number;
}

interface QuoteItemForm {
  id: string;
  productId?: string;
  description: string;
  quantity: number;
  unitPrice: number;
  taxRate: number;
}

interface Client {
  id: string;
  name: string;
  email: string;
  address?: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
}

interface Product {
  id: string;
  name: string;
  description?: string;
  price: number;
  taxRate: number;
  unit: string;
}

const mockClients: Client[] = [
  { id: '1', name: 'TechCorp Solutions', email: 'contact@techcorp.com' },
  { id: '2', name: 'Global Enterprises', email: 'procurement@global.com' },
  { id: '3', name: 'Startup Innovations', email: 'hello@startup.com' },
  { id: '4', name: 'Retail Solutions Ltd', email: 'info@retail.com' },
  { id: '5', name: 'Healthcare Systems', email: 'contact@healthcare.com' },
  { id: '6', name: 'HutsTech', email: 'segodi@hutstech.co.za' }
];

const mockProducts: Product[] = [
  { id: '1', name: 'Basic Website', description: 'Professional basic website with responsive design', price: 1499, taxRate: 15, unit: 'project' },
  { id: '2', name: 'Standard Website', description: 'Advanced website with custom features and CMS', price: 2499, taxRate: 15, unit: 'project' },
  { id: '3', name: 'Corporate Pack', description: 'Premium corporate website with advanced functionality', price: 3499, taxRate: 15, unit: 'project' },
  { id: '4', name: 'Starter eCommerce', description: 'Basic eCommerce website with essential features', price: 5499, taxRate: 15, unit: 'project' },
  { id: '5', name: 'Standard eCommerce', description: 'Full-featured eCommerce platform with advanced tools', price: 7999, taxRate: 15, unit: 'project' },
  { id: '6', name: 'Hosting', description: 'Monthly web hosting service with SSL and support', price: 439, taxRate: 15, unit: 'month' },
  { id: '7', name: 'Domain .co.za', description: 'South African domain registration (.co.za)', price: 129, taxRate: 15, unit: 'year' },
  { id: '8', name: 'Domain .com', description: 'International domain registration (.com)', price: 189, taxRate: 15, unit: 'year' },
  { id: '9', name: 'Domain .org', description: 'Organization domain registration (.org)', price: 199, taxRate: 15, unit: 'year' },
  { id: '10', name: 'Domain .org.za', description: 'South African organization domain (.org.za)', price: 165, taxRate: 15, unit: 'year' }
];

const clientOptions = mockClients.map(client => ({
  value: client.id,
  label: client.name,
  email: client.email
}));

const productOptions = mockProducts.map(product => ({
  value: product.id,
  label: product.name,
  price: product.price,
  description: product.description,
  taxRate: product.taxRate,
  unit: product.unit
}));

export function QuoteCreate() {
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showSaveIndicator, setShowSaveIndicator] = useState(false);
  const [showSendOptions, setShowSendOptions] = useState(false);
  const [quoteId, setQuoteId] = useState<string | null>(null); // Track quote ID
  const [isNewQuote, setIsNewQuote] = useState(true); // Track if this is a new quote
  const autosaveTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [isTyping, setIsTyping] = useState(false);

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isValid, isDirty }
  } = useForm<QuoteForm>({
    defaultValues: {
      issueDate: new Date(),
      validUntil: addDays(new Date(), 30),
      items: [{ id: uuidv4(), description: '', quantity: 1, unitPrice: 0, taxRate: 10 }],
      discountType: 'percentage',
      discountValue: 0,
      taxRate: 10,
      terms: 'Quotation Terms and Conditions\n\nBy accepting this quotation, the client agrees to the stated pricing, scope, and validity period. Any changes requested after acceptance may incur additional charges. Payment terms, unless otherwise specified, are due within 5 days from the invoice date. The provider reserves the right to revise pricing if the project scope changes or if there are unforeseen cost increases.'
    },
    mode: 'onChange'
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'items'
  });

  const watchedItems = watch('items');
  const watchedDiscountType = watch('discountType');
  const watchedDiscountValue = watch('discountValue');
  const watchedTaxRate = watch('taxRate');
  const watchedIssueDate = watch('issueDate');
  const watchedValidUntil = watch('validUntil');

  const handleAutoSave = useCallback(async () => {
    if (!isValid || isSaving) return;
    
    setIsSaving(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // For autosave, we don't create a new quote, just update the existing one
      if (isNewQuote && !quoteId) {
        // This is the first save, generate an ID and quote number
        const newQuoteId = uuidv4();
        const newQuoteNumber = generateQuoteNumber(newQuoteId);
        setQuoteId(newQuoteId);
        setIsNewQuote(false);
        console.log('First save - Created quote:', { id: newQuoteId, quoteNumber: newQuoteNumber });
      } else {
        console.log('Autosave - Updated existing quote:', { id: quoteId });
      }
      
      setLastSaved(new Date());
      setHasUnsavedChanges(false);
      setShowSaveIndicator(true);
      
      setTimeout(() => setShowSaveIndicator(false), 2000);
      
      // Only show toast for first save, not for every update
      if (isNewQuote && !quoteId) {
        toast.success('Quote auto-saved successfully!', {
          position: 'bottom-right',
          autoClose: 2000,
          hideProgressBar: true
        });
      }
    } catch (error) {
      toast.error('Auto-save failed');
    } finally {
      setIsSaving(false);
    }
  }, [isValid, isSaving, isNewQuote, quoteId]);

  // Optimized useEffect hooks to prevent frequent refreshes
  useEffect(() => {
    if (isDirty && !hasUnsavedChanges && !isSaving) {
      const debounceTimer = setTimeout(() => {
        setHasUnsavedChanges(true);
      }, 2000); // Wait 2 seconds after user stops typing

      return () => clearTimeout(debounceTimer);
    }
  }, [isDirty, hasUnsavedChanges, isSaving]);

  // Auto-save timer - only trigger when not actively typing and has changes
  useEffect(() => {
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
    }

    if (hasUnsavedChanges && isValid && !isSaving && !isTyping) {
      autosaveTimerRef.current = setTimeout(() => {
        handleAutoSave();
        autosaveTimerRef.current = null;
      }, 8000); // Auto-save after 8 seconds of inactivity
    }

    return () => {
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = null;
      }
    };
  }, [hasUnsavedChanges, isValid, isSaving, isTyping, handleAutoSave]);

  // Prevent multiple autosaves and reset state after saving
  useEffect(() => {
    if (isSaving) {
      setHasUnsavedChanges(false);
    }
  }, [isSaving]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = null;
      }
    };
  }, []);

  // Calculate totals
  const subtotal = watchedItems.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0);
  const discountAmount = watchedDiscountType === 'percentage' 
    ? (subtotal * watchedDiscountValue / 100)
    : watchedDiscountValue;
  const taxableAmount = subtotal - discountAmount;
  const taxAmount = taxableAmount * (watchedTaxRate / 100);
  const total = taxableAmount + taxAmount;

  // Calculate days until expiry
  const daysUntilExpiry = differenceInDays(watchedValidUntil, new Date());
  const isExpiringSoon = daysUntilExpiry <= 7 && daysUntilExpiry > 0;
  const isExpired = daysUntilExpiry < 0;

  const handleClientChange = (option: any) => {
    const client = mockClients.find(c => c.id === option.value);
    setSelectedClient(client || null);
    setValue('clientId', option.value);
  };

  const handleProductChange = (index: number, option: any) => {
    if (option) {
      const product = mockProducts.find(p => p.id === option.value);
      if (product) {
        setValue(`items.${index}.productId`, product.id);
        setValue(`items.${index}.description`, product.name);
        setValue(`items.${index}.unitPrice`, product.price);
        setValue(`items.${index}.taxRate`, product.taxRate);
      }
    }
  };

  const handleTypingStart = () => {
    setIsTyping(true);
    // Clear any existing autosave timer when user starts typing
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }
  };

  const handleTypingStop = () => {
    setIsTyping(false);
    // Set a new autosave timer after user stops typing
    if (hasUnsavedChanges && isValid && !isSaving) {
      autosaveTimerRef.current = setTimeout(() => {
        console.log('Typing stopped, autosave timer triggered for quote:', quoteId || 'new');
        handleAutoSave();
        autosaveTimerRef.current = null;
      }, 5000); // Auto-save 5 seconds after user stops typing
    }
  };

  const addItem = () => {
    append({
      id: uuidv4(),
      description: '',
      quantity: 1,
      unitPrice: 0,
      taxRate: 10
    });
  };

  const removeItem = (index: number) => {
    if (fields.length > 1) {
      remove(index);
    }
  };

  const onSubmit = async (data: QuoteForm) => {
    if (isSaving) {
      console.log('Preventing duplicate submission while already saving');
      return;
    }
    
    setIsSaving(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      let finalQuoteId = quoteId;
      let finalQuoteNumber = '';
      
      if (isNewQuote || !quoteId) {
        // This is a new quote or first save
        finalQuoteId = uuidv4();
        finalQuoteNumber = generateQuoteNumber(finalQuoteId);
        setQuoteId(finalQuoteId);
        setIsNewQuote(false);
        console.log('New quote created:', { id: finalQuoteId, quoteNumber: finalQuoteNumber });
      } else {
        // This is an existing quote being updated
        finalQuoteNumber = generateQuoteNumber(quoteId);
        console.log('Existing quote updated:', { id: finalQuoteId, quoteNumber: finalQuoteNumber });
      }
      
      const quoteData = {
        id: finalQuoteId,
        ...data,
        subtotal,
        discountAmount,
        taxAmount,
        total,
        quoteNumber: finalQuoteNumber,
        status: 'draft' as const
      };
      
      console.log('Quote data:', quoteData);
      setLastSaved(new Date());
      setHasUnsavedChanges(false);
      toast.success('Quote saved successfully!');
      
      // In a real app, you would redirect to the quote list or view page
    } catch (error) {
      console.error('Error saving quote:', error);
      toast.error('Failed to save quote');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSendQuote = () => {
    setShowSendOptions(true);
  };

  const handleSendLink = async () => {
    setIsSending(true);
    setShowSendOptions(false);
    try {
      // Simulate API call to generate and send link
      await new Promise(resolve => setTimeout(resolve, 1500));
      toast.success('Quote link sent successfully!');
    } catch (error) {
      toast.error('Failed to send quote link');
    } finally {
      setIsSending(false);
    }
  };

  const handleSendCopy = async () => {
    setIsSending(true);
    setShowSendOptions(false);
    try {
      // Simulate API call to send copy
      await new Promise(resolve => setTimeout(resolve, 1500));
      toast.success('Quote copy sent successfully!');
    } catch (error) {
      toast.error('Failed to send quote copy');
    } finally {
      setIsSending(false);
    }
  };

  const generateQuoteNumber = (existingId?: string) => {
    if (existingId) {
      // For existing quotes, derive a consistent number from the ID
      return `Q-${new Date().getFullYear()}-${String(parseInt(existingId.slice(-6), 16) % 1000).padStart(3, '0')}`;
    }
    // For new quotes, use timestamp to ensure uniqueness
    const timestamp = Date.now();
    return `Q-${new Date().getFullYear()}-${String(timestamp % 1000).padStart(3, '0')}`;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  return (
    <motion.div 
      className={styles.container}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h1 className={styles.title}>Create Quote</h1>
          <p className={styles.subtitle}>Create a professional quote for your client</p>
          
          {/* Save Status Indicator */}
          <div className={styles.saveStatus}>
            {quoteId && (
              <div className={styles.quoteStatus}>
                <FiFileText size={14} />
                <span>Quote ID: {quoteId.slice(0, 8)}...</span>
                <span className={styles.quoteType}>
                  {isNewQuote ? 'New Draft' : 'Saved Draft'}
                </span>
              </div>
            )}
            
            {showSaveIndicator && (
              <motion.div 
                className={styles.saveIndicator}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
              >
                <FiCheckCircle size={16} />
                <span>Auto-saved</span>
              </motion.div>
            )}
            
            {lastSaved && (
              <div className={styles.lastSaved}>
                <FiClock size={14} />
                <span>Last saved: {format(lastSaved, 'MMM dd, HH:mm')}</span>
              </div>
            )}
            
                         {hasUnsavedChanges && (
               <div className={styles.unsavedChanges}>
                 <FiAlertCircle size={14} />
                 <span>Unsaved changes</span>
                 {!isTyping && (
                   <span className={styles.autosavePending}>Auto-save pending...</span>
                 )}
                 {isTyping && (
                   <span className={styles.typingIndicator}>Typing...</span>
                 )}
               </div>
             )}
          </div>
        </div>
        
        <div className={styles.headerActions}>
          <button 
            onClick={() => setIsPreviewMode(!isPreviewMode)}
            className={styles.previewButton}
          >
            {isPreviewMode ? <FiX size={16} /> : <FiEye size={16} />}
            {isPreviewMode ? 'Edit Mode' : 'Preview'}
          </button>
          <button 
            onClick={handleSendQuote}
            disabled={!isValid || isSending}
            className={`${styles.sendButton} ${isSending ? styles.loading : ''}`}
          >
            <FiSend size={16} />
            {isSending ? 'Sending...' : 'Send Quote'}
          </button>
          <button 
            onClick={handleSubmit(onSubmit)}
            disabled={!isValid || isSaving}
            className={`${styles.saveButton} ${isSaving ? styles.loading : ''}`}
            title={isSaving ? 'Saving quote...' : 'Save quote as draft'}
          >
            <FiSave size={16} />
            {isSaving ? 'Saving...' : 'Save Draft'}
          </button>
        </div>
      </div>

      <div className={styles.content}>
        {/* Quote Form */}
        <motion.div 
          className={styles.formSection}
          animate={{ width: isPreviewMode ? '50%' : '100%' }}
          transition={{ duration: 0.3 }}
        >
          <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
            {/* Client Information */}
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>
                <FiUser size={20} />
                Client Information
              </h2>
              <div className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <label>Client *</label>
                  <Select
                    options={clientOptions}
                    onChange={handleClientChange}
                    placeholder="Select a client"
                    className={styles.select}
                    classNamePrefix="select"
                  />
                  {errors.clientId && (
                    <span className={styles.error}>{errors.clientId.message}</span>
                  )}
                </div>
              </div>
              
              {selectedClient && (
                <motion.div 
                  className={styles.clientInfo}
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  transition={{ duration: 0.3 }}
                >
                  <div className={styles.clientCard}>
                    <h3>{selectedClient.name}</h3>
                    <p>{selectedClient.email}</p>
                    {selectedClient.address && (
                      <p>
                        {selectedClient.address.street}, {selectedClient.address.city}, {selectedClient.address.state} {selectedClient.address.zipCode}
                      </p>
                    )}
                  </div>
                </motion.div>
              )}
            </div>

            {/* Quote Details */}
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>
                <FiFileText size={20} />
                Quote Details
              </h2>
              <div className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <label>Quote Number</label>
                  <input
                    type="text"
                    value={generateQuoteNumber()}
                    readOnly
                    className={styles.input}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Issue Date *</label>
                  <DatePicker
                    selected={watchedIssueDate}
                    onChange={(date) => setValue('issueDate', date || new Date())}
                    className={styles.input}
                    dateFormat="MMM dd, yyyy"
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Valid Until *</label>
                  <div className={styles.dateInputGroup}>
                    <DatePicker
                      selected={watchedValidUntil}
                      onChange={(date) => setValue('validUntil', date || addDays(new Date(), 30))}
                      className={styles.input}
                      dateFormat="MMM dd, yyyy"
                      minDate={watchedIssueDate}
                    />
                    {isExpired && (
                      <div className={styles.expiryWarning}>
                        <FiAlertCircle size={14} />
                        <span>Expired</span>
                      </div>
                    )}
                    {isExpiringSoon && !isExpired && (
                      <div className={styles.expiryWarning}>
                        <FiClock size={14} />
                        <span>Expires in {daysUntilExpiry} days</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Items */}
            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <h2 className={styles.sectionTitle}>
                  <FiPackage size={20} />
                  Items
                </h2>
                <button type="button" onClick={addItem} className={styles.addItemButton}>
                  <FiPlus size={16} />
                  Add Item
                </button>
              </div>
              
              <div className={styles.itemsContainer}>
                <AnimatePresence>
                  {fields.map((field, index) => (
                    <motion.div
                      key={field.id}
                      className={styles.itemRow}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      transition={{ duration: 0.2 }}
                    >
                      <div className={styles.itemGrid}>
                        <div className={styles.formGroup}>
                          <label>Product/Service</label>
                          <Select
                            options={productOptions}
                            onChange={(option) => handleProductChange(index, option)}
                            placeholder="Select product or enter description"
                            className={styles.select}
                            isClearable
                          />
                        </div>
                        
                        <div className={styles.formGroup}>
                          <label>Description *</label>
                                                     <input
                             {...register(`items.${index}.description` as const, { required: 'Description is required' })}
                             className={styles.input}
                             placeholder="Item description"
                             onFocus={handleTypingStart}
                             onBlur={handleTypingStop}
                           />
                          {errors.items?.[index]?.description && (
                            <span className={styles.error}>{errors.items[index]?.description?.message}</span>
                          )}
                        </div>
                        
                        <div className={styles.formGroup}>
                          <label>Quantity *</label>
                          <input
                            type="number"
                            {...register(`items.${index}.quantity` as const, { 
                              required: 'Quantity is required',
                              min: { value: 1, message: 'Quantity must be at least 1' }
                            })}
                            className={styles.input}
                            min="1"
                            step="1"
                          />
                          {errors.items?.[index]?.quantity && (
                            <span className={styles.error}>{errors.items[index]?.quantity?.message}</span>
                          )}
                        </div>
                        
                        <div className={styles.formGroup}>
                          <label>Unit Price *</label>
                          <div className={styles.inputGroup}>
                            <span className={styles.inputPrefix}>$</span>
                            <input
                              type="number"
                              {...register(`items.${index}.unitPrice` as const, { 
                                required: 'Unit price is required',
                                min: { value: 0, message: 'Unit price must be positive' }
                              })}
                              className={styles.input}
                              min="0"
                              step="0.01"
                            />
                          </div>
                          {errors.items?.[index]?.unitPrice && (
                            <span className={styles.error}>{errors.items[index]?.unitPrice?.message}</span>
                          )}
                        </div>
                        
                        <div className={styles.formGroup}>
                          <label>Tax Rate (%)</label>
                          <input
                            type="number"
                            {...register(`items.${index}.taxRate` as const)}
                            className={styles.input}
                            min="0"
                            max="100"
                            step="0.1"
                          />
                        </div>
                        
                        <div className={styles.formGroup}>
                          <label>Total</label>
                          <div className={styles.itemTotal}>
                            {formatCurrency(watchedItems[index]?.quantity * watchedItems[index]?.unitPrice || 0)}
                          </div>
                        </div>
                        
                        <div className={styles.itemActions}>
                          <button
                            type="button"
                            onClick={() => removeItem(index)}
                            className={styles.removeItemButton}
                            disabled={fields.length === 1}
                          >
                            <FiTrash2 size={16} />
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            </div>

            {/* Discounts and Taxes */}
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>
                <FiPercent size={20} />
                Discounts & Taxes
              </h2>
              <div className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <label>Discount Type</label>
                  <select {...register('discountType')} className={styles.input}>
                    <option value="percentage">Percentage (%)</option>
                    <option value="fixed">Fixed Amount ($)</option>
                  </select>
                </div>
                
                <div className={styles.formGroup}>
                  <label>Discount Value</label>
                  <div className={styles.inputGroup}>
                    {watchedDiscountType === 'percentage' && <span className={styles.inputPrefix}>%</span>}
                    {watchedDiscountType === 'fixed' && <span className={styles.inputPrefix}>$</span>}
                    <input
                      type="number"
                      {...register('discountValue', { min: 0 })}
                      className={styles.input}
                      min="0"
                      step={watchedDiscountType === 'percentage' ? '0.1' : '0.01'}
                    />
                  </div>
                </div>
                
                <div className={styles.formGroup}>
                  <label>Tax Rate (%)</label>
                  <input
                    type="number"
                    {...register('taxRate', { min: 0, max: 100 })}
                    className={styles.input}
                    min="0"
                    max="100"
                    step="0.1"
                  />
                </div>
              </div>
              
              {/* Summary */}
              <div className={styles.summaryGrid}>
                <div className={styles.summaryItem}>
                  <span>Subtotal:</span>
                  <span>{formatCurrency(subtotal)}</span>
                </div>
                {discountAmount > 0 && (
                  <div className={styles.summaryItem}>
                    <span>Discount:</span>
                    <span>-{formatCurrency(discountAmount)}</span>
                  </div>
                )}
                <div className={styles.summaryItem}>
                  <span>Tax ({watchedTaxRate}%):</span>
                  <span>{formatCurrency(taxAmount)}</span>
                </div>
                <div className={styles.summaryTotal}>
                  <span>Total:</span>
                  <span>{formatCurrency(total)}</span>
                </div>
              </div>
            </div>

            {/* Notes and Terms */}
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>
                <FiFileText size={20} />
                Additional Information
              </h2>
              <div className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <label>Notes</label>
                                     <textarea
                     {...register('notes')}
                     className={styles.textarea}
                     placeholder="Additional notes for the client..."
                     rows={3}
                     onFocus={handleTypingStart}
                     onBlur={handleTypingStop}
                   />
                </div>
                
                <div className={styles.formGroup}>
                  <label>Terms & Conditions</label>
                                     <textarea
                     {...register('terms')}
                     className={styles.textarea}
                     placeholder="Payment terms and conditions..."
                     rows={3}
                     onFocus={handleTypingStart}
                     onBlur={handleTypingStop}
                   />
                </div>
              </div>
            </div>
          </form>
        </motion.div>

        {/* Preview Panel */}
        <AnimatePresence>
          {isPreviewMode && (
            <motion.div 
              className={styles.previewSection}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 50 }}
              transition={{ duration: 0.3 }}
            >
              <div className={styles.previewHeader}>
                <h2>Quote Preview</h2>
                <button className={styles.downloadButton}>
                  <FiDownload size={16} />
                  Download PDF
                </button>
              </div>
              
              <div className={styles.previewContent}>
                <div className={styles.quotePreview}>
                  <div className={styles.quoteHeader}>
                    <div className={styles.companyInfo}>
                      <h1>C-Band</h1>
                      <p>Professional Invoicing & Accounting</p>
                      <p>info@c-band.com</p>
                    </div>
                    <div className={styles.quoteInfo}>
                      <h2>QUOTE</h2>
                      <p><strong>Quote #:</strong> {generateQuoteNumber()}</p>
                      <p><strong>Date:</strong> {format(watchedIssueDate, 'MMM dd, yyyy')}</p>
                      <p><strong>Valid Until:</strong> {format(watchedValidUntil, 'MMM dd, yyyy')}</p>
                      {isExpired && <p className={styles.expiredBadge}>EXPIRED</p>}
                      {isExpiringSoon && !isExpired && <p className={styles.expiringBadge}>Expires in {daysUntilExpiry} days</p>}
                    </div>
                  </div>
                  
                  {selectedClient && (
                    <div className={styles.clientSection}>
                      <h3>Bill To:</h3>
                      <p><strong>{selectedClient.name}</strong></p>
                      <p>{selectedClient.email}</p>
                      {selectedClient.address && (
                        <p>
                          {selectedClient.address.street}<br />
                          {selectedClient.address.city}, {selectedClient.address.state} {selectedClient.address.zipCode}
                        </p>
                      )}
                    </div>
                  )}
                  
                  <div className={styles.itemsTable}>
                    <table>
                      <thead>
                        <tr>
                          <th>Description</th>
                          <th>Qty</th>
                          <th>Unit Price</th>
                          <th>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {watchedItems.map((item, index) => (
                          <tr key={index}>
                            <td>{item.description}</td>
                            <td>{item.quantity}</td>
                            <td>{formatCurrency(item.unitPrice)}</td>
                            <td>{formatCurrency(item.quantity * item.unitPrice)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  <div className={styles.totalsSection}>
                    <div className={styles.totalRow}>
                      <span>Subtotal:</span>
                      <span>{formatCurrency(subtotal)}</span>
                    </div>
                    {discountAmount > 0 && (
                      <div className={styles.totalRow}>
                        <span>Discount:</span>
                        <span>-{formatCurrency(discountAmount)}</span>
                    </div>
                    )}
                    <div className={styles.totalRow}>
                      <span>Tax ({watchedTaxRate}%):</span>
                      <span>{formatCurrency(taxAmount)}</span>
                    </div>
                    <div className={styles.totalRow}>
                      <strong>Total:</strong>
                      <strong>{formatCurrency(total)}</strong>
                    </div>
                  </div>
                  
                  {watch('notes') && (
                    <div className={styles.notesSection}>
                      <h3>Notes:</h3>
                      <p>{watch('notes')}</p>
                    </div>
                  )}
                  
                  {watch('terms') && (
                    <div className={styles.termsSection}>
                      <h3>Terms & Conditions:</h3>
                      <p>{watch('terms')}</p>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Send Options Modal */}
        <AnimatePresence>
          {showSendOptions && (
            <motion.div
              className={styles.modalOverlay}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <motion.div
                className={styles.modal}
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                transition={{ duration: 0.2 }}
              >
                <div className={styles.modalHeader}>
                  <h3 className={styles.modalTitle}>Send Quote</h3>
                  <button
                    onClick={() => setShowSendOptions(false)}
                    className={styles.modalCloseButton}
                  >
                    <FiX size={20} />
                  </button>
                </div>
                
                <div className={styles.modalContent}>
                  <p className={styles.modalDescription}>
                    Choose how you would like to send this quote:
                  </p>
                  
                  <div className={styles.sendOptions}>
                    <button
                      onClick={handleSendLink}
                      disabled={isSending}
                      className={`${styles.sendOptionButton} ${styles.sendLinkButton}`}
                    >
                      <FiSend size={16} />
                      <div className={styles.sendOptionContent}>
                        <span className={styles.sendOptionTitle}>Send Link</span>
                        <span className={styles.sendOptionDescription}>
                          Send a secure link to the client to view the quote online
                        </span>
                      </div>
                    </button>
                    
                    <button
                      onClick={handleSendCopy}
                      disabled={isSending}
                      className={`${styles.sendOptionButton} ${styles.sendCopyButton}`}
                    >
                      <FiSend size={16} />
                      <div className={styles.sendOptionContent}>
                        <span className={styles.sendOptionTitle}>Send Copy</span>
                        <span className={styles.sendOptionDescription}>
                          Send a copy of the quote directly to the client's email
                        </span>
                      </div>
                    </button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}
